import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/**
 * The state and the actions both of Platform Administration's identity lists need — Humans
 * and Machines — built once, the way the administration client SDK's `defineCollectionStore`
 * builds its five. It is not that factory: this SDK depends up into no host SDK, and the
 * identity server's lists carry no FQL search, no searchable surface and no PATCH, so what is
 * shared here is the smaller half of it.
 *
 * **Every action resolves, and none of them throws.** A failure lands in `error` as the
 * server's own word (`NOT_FOUND`, `MACHINE_RETIRED`) or this store's own when there was no
 * server to answer (`ADMINISTRATION_REQUEST_FAILED`), and the action answers `false`. The
 * alternative — every caller wrapping every action in a `try` — is one missing `try` away from
 * an unhandled rejection in a click handler.
 *
 * @param {Object} config
 * @param {string} config.id       The Pinia store id.
 * @param {Object} config.client   The noun's methods off the transport: `list` and `read` at least.
 * @param {Function} [config.extend] Called inside the setup with `{ client, attempt }`, and
 *   whatever it returns joins the store. It runs inside `defineStore` so the state it creates
 *   is that store's, not a module's.
 */
export function defineAdministrationCollectionStore({ id, client, extend }) {
    return defineStore(id, function () {
        // The page currently held, and what asked for it.
        const documents = ref([]);
        const totalCount = ref(0);
        const page = ref(1);
        const pageSize = ref(null);
        // The list's own parameters beyond paging — a person's `q`, a program's `status` — held
        // with the page, because page two of a filtered list is that filter again.
        const filter = ref({});

        // The one record a detail panel is looking at.
        const current = ref(null);

        const loading = ref(false);
        const error = ref(null);

        // Whether a list has been read yet: `refresh` has nothing to repeat before one has.
        const listed = ref(false);

        const pageCount = computed(function () {
            if (pageSize.value === null || pageSize.value <= 0) {
                return null;
            }
            return Math.ceil(totalCount.value / pageSize.value);
        });

        /**
         * Run one call against the transport, and let its failure land in `error` rather than
         * in the caller. `loading` is set and cleared here so no action can forget to clear it
         * on the failing path, which is the path that matters.
         */
        async function attempt(work) {
            loading.value = true;
            error.value = null;
            try {
                const value = await work();
                loading.value = false;
                return { ok: true, value };
            } catch (err) {
                error.value = err?.body?.error || 'ADMINISTRATION_REQUEST_FAILED';
                loading.value = false;
                return { ok: false };
            }
        }

        /**
         * A page of records. `filter`, `page` and `pageSize` are held once a caller names them;
         * an absent one is not sent, so the server's own defaults apply.
         */
        async function list(options = {}) {
            if (options.filter !== undefined) {
                filter.value = options.filter;
            }
            if (options.page !== undefined) {
                page.value = options.page;
            }
            if (options.pageSize !== undefined) {
                pageSize.value = options.pageSize;
            }
            const attempted = await attempt(function () {
                return client.list({
                    ...filter.value,
                    page: page.value,
                    pageSize: pageSize.value ?? undefined
                });
            });
            if (attempted.ok === false) {
                return false;
            }
            listed.value = true;
            documents.value = attempted.value.documents;
            totalCount.value = attempted.value.meta.totalCount;
            return true;
        }

        /** Read one record into `current`. */
        async function load(recordId) {
            const attempted = await attempt(function () {
                return client.read(recordId);
            });
            if (attempted.ok === false) {
                return false;
            }
            current.value = attempted.value;
            return true;
        }

        /**
         * Read the held page again the way it was read, and the held record again if there is
         * one. Every write on these routes answers a stub rather than the record, so a screen
         * that just wrote is looking at a stale row until it asks again. Deliberately not
         * automatic after a write: a caller about to write twice more would pay for three reads.
         *
         * Answers `false` when nothing has been read yet, or when either read was refused.
         */
        async function refresh() {
            if (listed.value === false) {
                return false;
            }
            const pageOk = await list();
            if (current.value === null) {
                return pageOk;
            }
            const recordOk = await load(current.value.id);
            return pageOk && recordOk;
        }

        const shared = {
            documents,
            totalCount,
            page,
            pageSize,
            filter,
            current,
            loading,
            error,
            pageCount,
            attempt,
            list,
            load,
            refresh
        };

        if (extend === undefined) {
            return shared;
        }
        // `attempt` is handed over so an added action fails the same way every shared one
        // does — into `error`, answering false, with `loading` cleared.
        return { ...shared, ...extend({ client, attempt }) };
    });
}

export default defineAdministrationCollectionStore;
